import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, MapPin, RefreshCcw } from 'lucide-react'
import { userClient } from '../../lib/userClient'

type RecommendedContentType = 'all' | 'trip' | 'post' | 'event'

interface RecommendedItem {
  id: string
  type: 'trip' | 'post' | 'event'
  title?: string | null
  description?: string | null
  summary?: string | null
  img?: string | null
  coverImage?: string | null
  createdAt?: string
  created_at?: string
  creator?: {
    id: string
    name: string
    avatar?: string | null
  }
  locations?: Array<{
    title?: string | null
    name?: string | null
    latitude?: number | null
    longitude?: number | null
  }>
  stats?: {
    likes?: number
    comments?: number
    participantCount?: number
    favoritesCount?: number
  }
}

interface RecommendedResponse {
  data: RecommendedItem[]
  count: number
  strategy: string
}

export default function UserHomePage() {
  const navigate = useNavigate()
  const [contentType, setContentType] = useState<RecommendedContentType>('all')
  const [items, setItems] = useState<RecommendedItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [strategy, setStrategy] = useState<string | null>(null)

  const displayItems = useMemo(() => items ?? [], [items])

  const fetchRecommended = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await userClient.getRecommended({
        contentType,
        limit: 12,
      })
      const payload = response.data as RecommendedResponse
      setItems(payload.data)
      setStrategy(payload.strategy)
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to load recommended content'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [contentType])

  useEffect(() => {
    fetchRecommended()
  }, [fetchRecommended])

  return (
    <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Recommended for you</h2>
          <p className="text-sm text-gray-600">
            Pulls data directly from <code>api.waybook.me/recommended</code>
            {strategy ? (
              <span className="ml-1 text-gray-500">({strategy} strategy)</span>
            ) : null}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <select
            value={contentType}
            onChange={(event) => setContentType(event.target.value as RecommendedContentType)}
            className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
          >
            <option value="all">All content</option>
            <option value="trip">Trips</option>
            <option value="event">Events</option>
            <option value="post">Posts</option>
          </select>
          <button
            type="button"
            onClick={fetchRecommended}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
          >
            <RefreshCcw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
          </div>
        ) : error ? (
          <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : displayItems.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 px-6 py-10 text-center text-sm text-gray-500">
            No content returned for this filter.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {displayItems.map((item) => (
              <article
                key={`${item.type}-${item.id}`}
                className="border border-gray-200 rounded-lg p-4 hover:shadow transition bg-white cursor-pointer"
                onClick={() => {
                  if (item.type === 'trip') {
                    navigate(`/user/trips/${item.id}`)
                  }
                }}
              >
                <div className="flex items-center justify-between">
                  <span className="uppercase text-xs font-semibold tracking-wide text-gray-500">
                    {item.type}
                  </span>
                  <span className="text-xs text-gray-400">
                    {new Date(item.createdAt || item.created_at || Date.now()).toLocaleString()}
                  </span>
                </div>

                <h3 className="mt-2 text-base font-semibold text-gray-900">
                  {item.title || item.summary || 'Untitled'}
                </h3>

                {item.description && (
                  <p className="mt-2 text-sm text-gray-600 line-clamp-3">
                    {item.description}
                  </p>
                )}

                {item.locations && item.locations.length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    <MapPin className="h-3.5 w-3.5" />
                    <span>{item.locations.map((loc) => loc.title || loc.name).join(', ')}</span>
                  </div>
                )}

                {item.creator && item.creator.name && (
                  <div className="mt-4 flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-gray-100 overflow-hidden">
                      {item.creator.avatar ? (
                        <img
                          src={item.creator.avatar}
                          alt={item.creator.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-xs text-gray-600">
                          {item.creator.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{item.creator.name}</div>
                      <div className="text-xs text-gray-500">{item.creator.id}</div>
                    </div>
                  </div>
                )}

                {item.stats && (
                  <dl className="mt-4 grid grid-cols-3 gap-2 text-center">
                    {'likes' in item.stats && (
                      <div className="rounded-lg bg-gray-50 py-2">
                        <dt className="text-[11px] uppercase tracking-wide text-gray-500">Likes</dt>
                        <dd className="text-sm font-semibold text-gray-900">{item.stats.likes ?? 0}</dd>
                      </div>
                    )}
                    {'comments' in item.stats && (
                      <div className="rounded-lg bg-gray-50 py-2">
                        <dt className="text-[11px] uppercase tracking-wide text-gray-500">Comments</dt>
                        <dd className="text-sm font-semibold text-gray-900">{item.stats.comments ?? 0}</dd>
                      </div>
                    )}
                    {'participantCount' in item.stats && (
                      <div className="rounded-lg bg-gray-50 py-2">
                        <dt className="text-[11px] uppercase tracking-wide text-gray-500">Participants</dt>
                        <dd className="text-sm font-semibold text-gray-900">
                          {item.stats.participantCount ?? 0}
                        </dd>
                      </div>
                    )}
                  </dl>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
